/**
 * Algolia search for the header search modal — Asana 1217333292818333.
 *
 * Talks to Algolia's REST endpoint with `$fetch` rather than pulling in
 * `@nuxtjs/algolia`, which is what architecture.org uses. Three reasons: we need
 * exactly one query call, the search key is public by design, and adding a Nuxt
 * module is build-level risk on a Cloudflare Workers deploy that has already
 * proven fragile. The module buys nothing we'd use here.
 *
 * INDEX: architecture.org publishes `staging_Index` and `production_Index`, and
 * we share their account so OHC results can eventually sit alongside theirs.
 * Which one we hit follows the deploy, not the browser.
 *
 * The index is SHARED with architecture.org, so the query is filtered to the two
 * OHC models — otherwise an OHC search box returns their tours and buildings.
 * Their records stay searchable from their own site, unaffected.
 */

/**
 * Restricts results to OHC's own records. `model` carries these exact strings
 * from /api/algolia/records, and must be in the index's attributesForFaceting —
 * when it isn't, Algolia silently returns 0 hits rather than erroring, which is
 * an easy failure to misread as "no results".
 */
export const OHC_MODELS = [
    'Open House Chicago - Sites',
    'Open House Chicago - Neighborhoods',
    'Open House Chicago - Events',
] as const;

export const OHC_FILTER = OHC_MODELS.map((m) => `model:"${m}"`).join(' OR ');

/** Builds the Algolia `params` string. Separated out so it can be asserted on. */
export function buildSearchParams(term: string, hitsPerPage = 8): string {
    return [
        `query=${encodeURIComponent(term)}`,
        `hitsPerPage=${hitsPerPage}`,
        `filters=${encodeURIComponent(OHC_FILTER)}`,
    ].join('&');
}

export interface SearchHit {
    objectID: string;
    /** Rendered as the label above the title, e.g. "Tour" or "Building". */
    model?: string;
    title?: string;
    description?: string;
    url?: string;
    seo?: { title?: string; description?: string };
}

/** Strips tags and entities, then truncates on a word boundary. */
export function cleanDescription(raw: unknown, max = 160): string {
    if (typeof raw !== 'string' || !raw.trim()) return '';
    const text = raw
        .replace(/<[^>]*>/g, ' ')
        .replace(/&[a-z0-9#]+;/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    if (text.length <= max) return text;
    // Drop the partial last word rather than cutting mid-syllable.
    return `${text.slice(0, max).split(' ').slice(0, -1).join(' ')}…`;
}

/**
 * Resolves a hit's url, and says whether it leaves OHC.
 *
 * Records currently come from architecture.org and carry site-relative urls
 * (`/city-tours/...`), so they need that origin bolted on. Once OHC records are
 * indexed they will carry absolute OHC urls and resolve as internal.
 *
 * `external` drives target="_blank": following an architecture.org result would
 * otherwise drop the visitor out of OHC and out of the iOS/Android wrapper with
 * no way back — the same reason the footer's links open in a new window.
 */
/**
 * Every hostname OHC has been served from. A record indexed under any of these
 * is OUR page on ANOTHER of our hosts, so the path is the real content and the
 * host is an accident of which deploy the crawler happened to read.
 *
 * This is not hypothetical. Production currently queries `staging_Index` — see
 * resolveIndex below — so every result it returns carries a
 * staging.app.openhousechicago.org url. Linked verbatim, searching on the live
 * site walked visitors onto staging mid-session.
 */
const OHC_HOSTS = new Set([
    'openhousechicago.org',
    'www.openhousechicago.org',
    'app.openhousechicago.org',
    'staging.app.openhousechicago.org',
    'ohc.architecture.org',
]);

export function resolveHitHref(url: unknown, ownOrigin: string): { href: string; external: boolean } {
    const raw = typeof url === 'string' ? url.trim() : '';
    if (!raw) return { href: '', external: false };

    const absolute = /^https?:\/\//i.test(raw)
        ? raw
        : `https://www.architecture.org${raw.startsWith('/') ? '' : '/'}${raw}`;

    try {
        const target = new URL(absolute);
        const own = new URL(ownOrigin);

        // One of ours on a different OHC host: keep the visitor where they are.
        // pathname + search + hash, so a deep link survives the move intact.
        if (target.host !== own.host && OHC_HOSTS.has(target.host)) {
            return { href: `${own.origin}${target.pathname}${target.search}${target.hash}`, external: false };
        }

        return { href: absolute, external: target.host !== own.host };
    } catch {
        // Unparseable own-origin (or a malformed url): treat as external, which
        // is the safe direction — a new tab is recoverable, a lost session isn't.
        return { href: absolute, external: true };
    }
}

/** Title and description both have an SEO override upstream. */
export function hitTitle(hit: SearchHit): string {
    return hit?.seo?.title || hit?.title || '';
}

export function hitDescription(hit: SearchHit, max = 160): string {
    return cleanDescription(hit?.seo?.description || hit?.description, max);
}

/**
 * Which index this deploy searches. Staging and local share one so QA sees the
 * same records; production has its own.
 */
/**
 * Which Algolia index a deploy queries.
 *
 * Matched on the parsed HOST, not a substring of the url. The previous version
 * tested the raw string for `//ohc.architecture.org` or
 * `//app.openhousechicago.org` — neither of which matches the apex domain
 * production actually serves from, so production silently fell through to
 * `staging_Index` and every search result linked visitors onto staging. It went
 * unnoticed because the fallback returns a working index, just the wrong one.
 *
 * Unknown hosts (localhost, preview deploys, anything new) get `staging_Index`.
 * That is the safe default: a developer seeing staging data is a much smaller
 * problem than a visitor being handed it.
 *
 * OHC records live alongside architecture.org's in the same index — they were
 * crawled into production_Index on 2026-09-22 — which is why every query here
 * carries OHC_FILTER. Without it an OHC search returns their tours and
 * buildings.
 */
const PRODUCTION_HOSTS = new Set([
    'openhousechicago.org',
    'www.openhousechicago.org',
    'app.openhousechicago.org',
    'ohc.architecture.org',
]);

export function resolveIndex(configured: string, frontend: string): string {
    if (configured) return configured;

    let host = '';
    try {
        host = new URL(frontend).host.toLowerCase();
    } catch {
        return 'staging_Index';
    }

    return PRODUCTION_HOSTS.has(host) ? 'production_Index' : 'staging_Index';
}

export const useSiteSearch = () => {
    const config = useRuntimeConfig().public;
    const index = resolveIndex(config.algoliaIndex as string, String(config.clientFrontend || ''));

    const query = ref('');
    const hits = ref<SearchHit[]>([]);
    const loading = ref(false);
    const searched = ref(false);
    const failed = ref(false);

    let latest = 0;

    const run = async () => {
        const term = query.value.trim();

        if (!term) {
            hits.value = [];
            searched.value = false;
            loading.value = false;
            failed.value = false;
            return;
        }

        // Guards against a slow early request overwriting a later, faster one —
        // with a debounced input that reordering is easy to hit.
        const ticket = ++latest;
        loading.value = true;
        failed.value = false;

        try {
            const result = await $fetch<{ hits: SearchHit[] }>(
                `https://${config.algoliaAppId}-dsn.algolia.net/1/indexes/${index}/query`,
                {
                    method: 'POST',
                    headers: {
                        'X-Algolia-API-Key': String(config.algoliaSearchKey),
                        'X-Algolia-Application-Id': String(config.algoliaAppId),
                    },
                    body: { params: buildSearchParams(term) },
                },
            );
            if (ticket !== latest) return;
            hits.value = result?.hits ?? [];
        } catch (error) {
            if (ticket !== latest) return;
            // Search failing should show a message, not blow up the header.
            console.error('[search] Algolia query failed:', error);
            hits.value = [];
            failed.value = true;
        } finally {
            if (ticket === latest) {
                loading.value = false;
                searched.value = true;
            }
        }
    };

    return { query, hits, loading, searched, failed, run, index };
};
